
import React, { useState, useEffect } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
// useCompanyScrapbook removed
import { checkAdminAuthorization } from '@/utils/authHelpers';
import { useNavigate } from 'react-router-dom';
import { CompaniesFilter } from './CompaniesFilter';
import CompanyListView from './CompanyListView';
import CompanyTableView from './CompanyTableView';
import CompanyScreenshotLightbox from './CompanyScreenshotLightbox';
import { useCompaniesFilter } from '@/hooks/useCompaniesFilter';
import { Button } from '@/components/ui/button';
import { Download, Grid, List, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { forceRefreshCompaniesData } from '@/utils/companiesHelper';


interface CompaniesListProps {
  searchResults?: any[];
  onCompanySelect: (slug: string) => void;
  viewMode?: 'cards' | 'table';
  onViewModeChange?: (mode: 'cards' | 'table') => void;
}

const CompaniesList = ({ 
  searchResults, 
  onCompanySelect,
  viewMode = 'cards',
  onViewModeChange
}: CompaniesListProps) => {
  const companies = useAppSelector(state => state.supaCompanies.companies);
  const uniqueCategories = useAppSelector(state => state.supaCompanies.uniqueCategories);
  const uniqueEmployees = useAppSelector(state => state.supaCompanies.uniqueEmployees);
  // Project functionality removed
  const displayResults = searchResults || companies;

  // Log companies data to check tags
  useEffect(() => {
    if (displayResults.length > 0) {
      // Find companies with specific slugs for debugging
      const debugCompanies = displayResults.filter(c =>
        c.slug === 'intercom' || c.slug === 'shopify' || c.slug === 'github'
      );

      if (debugCompanies.length > 0) {
        console.log('🔍 [CompaniesList] Debug companies data:',
          debugCompanies.map(c => ({
            slug: c.slug,
            hasTags: Boolean(c.tags && c.tags.length > 0),
            tags: c.tags
          }))
        );
      }

      // Log overall tag statistics
      console.log('📊 [CompaniesList] Tag statistics:', {
        totalCompanies: displayResults.length,
        companiesWithTags: displayResults.filter(c => c.tags && c.tags.length > 0).length,
        tagsStructure: displayResults.find(c => c.tags && c.tags.length > 0)?.tags?.slice(0, 2) || 'No tags found'
      });
    }
  }, [displayResults]);
  const { toast } = useToast();

  const [isAdmin, setIsAdmin] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const navigate = useNavigate();

  const {
    selectedCategories,
    selectedEmployees,
    selectedProductCount,
    selectedSubcategories,
    selectedModalities,
    selectedTags,
    selectedDiscounts,
    selectedAddOns,
    slugFilter,
    uniqueSubcategories,
    uniqueModalities,
    uniqueTags,
    discountTypes,
    formatDiscountType,
    handleCategoryChange,
    handleEmployeesChange,
    handleProductCountChange,
    handleSubcategoryChange,
    handleModalityChange,
    handleTagChange,
    handleDiscountChange,
    handleAddOnsChange,
    handleSlugFilterChange,
    handleRemoveFilter,
    filteredCompanies,
    tagsLoading
  } = useCompaniesFilter(displayResults);

  // Scrapbook functionality removed

  useEffect(() => {
    const checkAdmin = async () => {
      const isAdminUser = await checkAdminAuthorization(navigate, (isAdmin) => {
        setIsAdmin(isAdmin);
      });
      setIsAdmin(isAdminUser);
    };
    checkAdmin();
  }, [navigate]);

  const handleCompanyClick = (index: number) => {
    // Navigate directly to edit page instead of opening lightbox
    const company = filteredCompanies[index];
    if (company) {
      console.log('👆 [CompaniesList] Company clicked:', company.slug, 'navigating to edit page');
      navigate(`/edit/${company.slug}`);
    }
  };

  const handleEditClick = (slug: string) => {
    console.log('✏️ [CompaniesList] Edit clicked for:', slug);
    onCompanySelect(slug);
  };

  const handleLightboxNavigate = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    } else if (direction === 'next' && lightboxIndex < filteredCompanies.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  const handleExportCSV = () => {
    console.log('📊 [CompaniesList] Exporting companies to CSV');
    try {
      // Get the filtered tags that are not 'All'
      const filteredTags = selectedTags.filter(tag => tag !== 'All');
      
      // Create base headers
      const baseHeaders = ['slug', 'category', 'employees', 'logoUrl'];
      
      // Add tag headers for each selected tag
      const tagHeaders = [];
      if (filteredTags.length > 0) {
        filteredTags.forEach(tag => {
          tagHeaders.push(`has_${tag.replace(/\s+/g, '_')}`);
          tagHeaders.push(`${tag.replace(/\s+/g, '_')}_explanation`);
        });
      }
      
      // Combine all headers
      const headers = [...baseHeaders, ...tagHeaders];
      
      // Generate rows for the CSV
      const rows = filteredCompanies.map(company => {
        // Base company data
        const baseData = [
          company.slug,
          company.category,
          company.employees || '',
          company.logoUrl || ''
        ];
        
        // Helper function to extract tag data from different tag formats
        const normalizeTag = (tag: any) => {
          if (!tag) return null;
          
          if (typeof tag === 'string') return { name: tag, explanation: '' };
          
          if (tag.name) {
            return { 
              name: tag.name, 
              explanation: tag.explanation || ''
            };
          }
          
          if (tag.tag && tag.tag.name) {
            return { 
              name: tag.tag.name, 
              explanation: tag.explanation || ''
            };
          }
          
          return null;
        };
        
        // Process tags for this company
        const companyTags = (company.tags || []).map(normalizeTag).filter(Boolean);
        
        // Add tag data for each selected tag
        const tagData = [];
        if (filteredTags.length > 0) {
          filteredTags.forEach(selectedTag => {
            // Find if this company has the selected tag
            const matchingTag = companyTags.find(tag => 
              tag?.name.toLowerCase() === selectedTag.toLowerCase()
            );
            
            // Add has_tag flag (Yes/No)
            tagData.push(matchingTag ? 'Yes' : 'No');
            
            // Add tag explanation if available, empty otherwise
            const explanation = matchingTag?.explanation || '';
            // Escape any commas, quotes, or newlines to prevent CSV format issues
            const escapedExplanation = explanation.replace(/"/g, '""')
              .replace(/\n/g, ' ')
              .replace(/\r/g, '');
            tagData.push(`"${escapedExplanation}"`);
          });
        }
        
        // Combine all data for this row
        return [...baseData, ...tagData].join(',');
      });
      
      // Create final CSV content
      const csvContent = [
        headers.join(','),
        ...rows
      ].join('\n');

      // Create filename with selected tags
      let filename = 'companies';
      if (filteredTags.length > 0) {
        filename += `_with_tags_${filteredTags.join('_')}`;
      }
      filename += '.csv';

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export successful",
        description: filteredTags.length > 0 ?
          `${filteredCompanies.length} companies with tag data exported to CSV` :
          `${filteredCompanies.length} companies exported to CSV`,
        duration: 3000
      });
    } catch (error) {
      console.error('❌ [CompaniesList] Error exporting CSV:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting the companies",
        variant: "destructive",
        duration: 3000
      });
    }
  };

  // Function to handle refreshing companies data
  const handleRefreshData = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      console.log('🔄 [CompaniesList] Refreshing companies data - clearing Redux first...');
      // Clear Redux store to force fresh GraphQL fetch
      const { setSupaCompanies } = await import('@/store/supaCompaniesSlice');
      const { store } = await import('@/store/store');
      store.dispatch(setSupaCompanies([]));
      
      await forceRefreshCompaniesData();
      
      toast({
        title: "Data refreshed",
        description: "Companies data has been refreshed successfully",
        duration: 3000
      });
    } catch (error) {
      console.error('❌ [CompaniesList] Error refreshing companies data:', error);
      toast({
        title: "Refresh failed",
        description: "There was an error refreshing the companies data",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sm font-medium text-gray-500">
            Showing {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
          </h2>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            
            {/* View Toggle with Refresh button next to it */}
            <div className="flex items-center">
              <ToggleGroup 
                type="single" 
                value={viewMode}
                onValueChange={(value) => {
                  if (value && onViewModeChange) {
                    onViewModeChange(value as 'cards' | 'table');
                  }
                }}
                className="border rounded-md"
              >
                <ToggleGroupItem value="table" aria-label="View as table" className="px-2 py-1 h-8">
                  <List className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="cards" aria-label="View as cards" className="px-2 py-1 h-8">
                  <Grid className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefreshData}
                disabled={isRefreshing}
                className="ml-1 px-2 h-8"
                title="Refresh Data"
                aria-label="Refresh Data"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
        <CompaniesFilter
          selectedCategories={selectedCategories}
          selectedEmployees={selectedEmployees}
          selectedProductCount={selectedProductCount}
          selectedModalities={selectedModalities}
          selectedDiscounts={selectedDiscounts}
          selectedAddOns={selectedAddOns}
          slugFilter={slugFilter}
          uniqueCategories={uniqueCategories}
          uniqueEmployees={uniqueEmployees}
          uniqueModalities={uniqueModalities}
          discountTypes={discountTypes}
          formatDiscountType={formatDiscountType}
          handleCategoryChange={handleCategoryChange}
          handleEmployeesChange={handleEmployeesChange}
          handleProductCountChange={handleProductCountChange}
          handleModalityChange={handleModalityChange}
          handleDiscountChange={handleDiscountChange}
          handleAddOnsChange={handleAddOnsChange}
          handleSlugFilterChange={handleSlugFilterChange}
          handleRemoveFilter={handleRemoveFilter}
        />
      </div>

      {viewMode === 'cards' ? (
        <CompanyListView
          companies={filteredCompanies}
          onCompanyClick={handleCompanyClick}
          onEditClick={handleEditClick}
          scrapbookEntries={[]}
          onScrapbookUpdate={() => {}}
        />
      ) : (
        <CompanyTableView
          companies={filteredCompanies}
          onCompanyClick={handleCompanyClick}
          onEditClick={handleEditClick}
          scrapbookEntries={[]}
        />
      )}

      {/* Lightbox disabled - row click now navigates to edit page
      <CompanyScreenshotLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        companies={filteredCompanies}
        currentIndex={lightboxIndex}
        onNavigate={handleLightboxNavigate}
      />
      */}
    </div>
  );
};

export default CompaniesList;
